import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { authAPI, settingsAPI } from '../utils/api';
import { clearPersistedFilters } from '../utils/usePersistentState';

const AuthContext = createContext(null);

const POLL_INTERVAL = 30_000; // check for permission changes every 30 seconds

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonatedAs, setImpersonatedAs] = useState(null); // session metadata when impersonating
  const [showAttribution, setShowAttribution] = useState(true); // global UI flag from settings
  const userRef = useRef(null); // track current user without triggering re-render

  // Load current impersonation session info from the backend.
  // Skips the call when there's no token in localStorage — without a token
  // the endpoint returns 401, which the axios interceptor turns into a
  // hard redirect to /login, which on first load (where there is no token
  // yet) creates an infinite SSO loop.
  const refreshImpersonation = useCallback(async () => {
    if (!localStorage.getItem('token')) {
      setImpersonatedAs(null);
      return;
    }
    try {
      const { data } = await authAPI.getActiveImpersonation();
      setImpersonatedAs(data.active ? data.session : null);
    } catch {
      setImpersonatedAs(null);
    }
  }, []);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      // No token at all — genuinely unauthenticated, show login normally (no error).
      setLoading(false);
      return;
    }

    try {
      const { data } = await authAPI.getMe();
      const fresh = data.user;

      // A real authenticated session exists now — reset the SSO loop-breaker so it
      // only ever counts CONSECUTIVE sign-in attempts that never reached an
      // authenticated session (see SSO_LOOP_KEY in Login.js). On mobile the SSO
      // round trip is slow and cross-origin; without clearing on success the
      // counter could linger and falsely trip a later sign-in.
      localStorage.removeItem('ssoLoopGuard');

      // If role or active status changed while the user was logged in, reload the
      // whole app so the new permissions take effect cleanly (nav, routes, data).
      if (userRef.current && (
        userRef.current.role   !== fresh.role ||
        userRef.current.active !== fresh.active
      )) {
        window.location.reload();
        return;
      }

      userRef.current = fresh;
      // Keep the SAME object when nothing changed. The 30s poll used to hand
      // every consumer a new user (and new role helpers) each time, so pages
      // whose effects depend on them reloaded — closing open dialogs and any
      // playing recording — every 30 seconds.
      setUser(prev => (prev && JSON.stringify(prev) === JSON.stringify(fresh) ? prev : fresh));
    } catch (err) {
      const isPolling = !!userRef.current; // true = user was already loaded, this is a bg poll

      // The 401 interceptor already restored the actor's own token after an
      // ended/expired impersonation and is navigating to /dashboard — clearing
      // storage here would log the actor out instead.
      if (err?.impersonationRestored) return;
      // The token was swapped while this request was in flight (a parallel
      // request or another tab restored the actor, or a new sign-in). It was
      // the OLD token that got rejected, so keep the new one and restart the
      // app on it rather than logging out.
      if (err?.authSuperseded) {
        window.location.assign('/dashboard');
        return;
      }

      if (isPolling && err?.response?.status !== 401) {
        // Polling failure due to transient backend issue (network hiccup, 503, restart).
        // Don't kick the user out — just skip this poll and try again in 30s.
        setLoading(false);
        return;
      }

      // Either initial load failure OR explicit 401 — clear auth.
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);

      // CRITICAL: redirect with an error param so Login.js shows a message instead of
      // auto-triggering Zoho SSO. Without ?error, Login.js immediately redirects to Zoho,
      // which succeeds, which brings the user back here, which fails again → infinite loop.
      window.location.replace('/login?error=session_expired');
      return;
    }
    setLoading(false);
  }, []);

  // Public attribution flag — pulled from /settings/impersonation/public.
  // Exposed via refreshAttribution() so the Settings page can force a refresh
  // immediately after the SuperAdmin saves a change, instead of waiting for
  // the user to reload the app.
  const refreshAttribution = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const { data } = await settingsAPI.getImpersonationPublicConfig();
      setShowAttribution(!!data.showImpersonatorAttribution);
    } catch { /* keep current value */ }
  }, []);

  // Initial load — refreshImpersonation runs after loadUser resolves so it
  // can't fire its API call before auth state is settled (or while there's
  // no token at all). Also fetch the public attribution flag.
  useEffect(() => {
    (async () => {
      await loadUser();
      await refreshImpersonation();
      await refreshAttribution();
    })();
  }, [loadUser, refreshImpersonation, refreshAttribution]);

  // Poll for permission / role changes
  useEffect(() => {
    const id = setInterval(() => {
      if (localStorage.getItem('token')) loadUser();
    }, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [loadUser]);

  const login = async (email, password) => {
    const { data } = await authAPI.login({ email, password });
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  };

  // Used by SSOCallback to store the JWT received from the SAML redirect
  const loginWithToken = (token, user) => {
    localStorage.setItem('token', token);
    if (user) localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
  };

  const logout = () => {
    clearPersistedFilters();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('originalToken');
    localStorage.removeItem('originalUser');
    setUser(null);
    setImpersonatedAs(null);
  };

  // Start impersonating another user. Stashes the actor's current token + user
  // under "original*" keys so endImpersonation() / the 401 interceptor can
  // restore the actor without forcing a re-login.
  const startImpersonation = async (targetUserId, reason) => {
    const { data } = await authAPI.startImpersonation(targetUserId, { reason });
    const actorToken = localStorage.getItem('token');
    const actorUser  = localStorage.getItem('user');
    if (actorToken) localStorage.setItem('originalToken', actorToken);
    if (actorUser)  localStorage.setItem('originalUser',  actorUser);
    localStorage.setItem('token', data.token);
    localStorage.setItem('user',  JSON.stringify(data.session.target));
    setUser(data.session.target);
    setImpersonatedAs(data.session);
    return data;
  };

  // End the active impersonation session.
  // If the caller is the actor, swap back to the stashed original token.
  // If the caller is the target (using their own token), just clear local state —
  // the actor's impersonation token will be rejected by the backend on next use.
  const endImpersonation = async () => {
    try { await authAPI.stopImpersonation(); } catch {}
    const originalToken = localStorage.getItem('originalToken');
    const originalUser  = localStorage.getItem('originalUser');
    if (originalToken) {
      localStorage.setItem('token', originalToken);
      if (originalUser) localStorage.setItem('user', originalUser);
      localStorage.removeItem('originalToken');
      localStorage.removeItem('originalUser');
      try { setUser(originalUser ? JSON.parse(originalUser) : null); } catch {}
      setImpersonatedAs(null);
      window.location.assign('/dashboard');
    } else {
      setImpersonatedAs(null);
    }
  };

  // Stable while the role is unchanged — pages list these in effect deps.
  const role = user?.role;
  const isSuperAdmin       = useCallback(() => role === 'SuperAdmin', [role]);
  const isRecruiterManager = useCallback(() => role === 'RecruiterManager', [role]);
  const isRecruiter        = useCallback(() => role === 'Recruiter', [role]);
  const hasRole            = useCallback((roles) => roles.includes(role), [role]);
  const isDeveloper = role === 'Developer';

  const value = useMemo(() => ({
    user, loading, login, loginWithToken, logout, loadUser,
    impersonatedAs, startImpersonation, endImpersonation, refreshImpersonation,
    showAttribution, refreshAttribution,
    isSuperAdmin, isRecruiterManager, isRecruiter, hasRole, isDeveloper,
  }), [user, loading, impersonatedAs, showAttribution, loadUser, refreshImpersonation, refreshAttribution, // eslint-disable-line
       isSuperAdmin, isRecruiterManager, isRecruiter, hasRole, isDeveloper]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
